import React, { useEffect, useMemo, useState } from "react";
import { Mail, Phone, RefreshCw, Search, Inbox, Building2, Briefcase, ExternalLink, Calendar } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function fmt(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; }
}

export default function JobApplications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [instituteFilter, setInstituteFilter] = useState("");
  const [jobFilter, setJobFilter] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (instituteFilter) params.institute_id = instituteFilter;
      if (jobFilter) params.job_id = jobFilter;
      const { data } = await api.get("/admin/job-applications", { params });
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load applications");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [instituteFilter, jobFilter]);

  const institutes = useMemo(() => {
    const m = new Map();
    items.forEach((i) => { if (i.institute_id && i.institute_name) m.set(i.institute_id, i.institute_name); });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  const jobs = useMemo(() => {
    const m = new Map();
    items.forEach((i) => {
      if (i.job_id && i.job_title && (!instituteFilter || i.institute_id === instituteFilter)) {
        m.set(i.job_id, `${i.job_title} — ${i.institute_name || ""}`);
      }
    });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [items, instituteFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      (i.full_name || "").toLowerCase().includes(q) ||
      (i.mobile || "").includes(q) ||
      (i.email || "").toLowerCase().includes(q) ||
      (i.institute_name || "").toLowerCase().includes(q) ||
      (i.job_title || "").toLowerCase().includes(q) ||
      (i.current_profession || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const byInstitute = (id) => items.filter((i) => i.institute_id === id).length;

  return (
    <div className="space-y-6" data-testid="admin-job-applications">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Trust CMS</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Job Applications</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">Every candidate application submitted across all institutes.</div>
        </div>
        <button type="button" onClick={load} data-testid="adm-jobapps-reload" className="inline-flex items-center gap-2 border border-[#e5e0d8] bg-white hover:bg-[#f5f1e8] text-[#234333] text-sm px-3 py-2 rounded-sm">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Applications" value={items.length} accent="navy" testid="adm-jobapps-stat-total" />
        <Stat label="Unique Candidates" value={new Set(items.map((i) => i.mobile)).size} accent="blue" testid="adm-jobapps-stat-candidates" />
        <Stat label="Open Roles" value={new Set(items.filter((i) => i.job_status === "Open").map((i) => i.job_id)).size} accent="emerald" testid="adm-jobapps-stat-open" />
        <Stat label="Institutes Represented" value={new Set(items.map((i) => i.institute_id)).size} accent="amber" testid="adm-jobapps-stat-institutes" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-white rounded-sm border border-[#e5e0d8] px-3 py-1.5 flex-1 min-w-[220px]">
          <Search size={14} className="text-[#6b655b]" />
          <input data-testid="adm-jobapps-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, mobile, email, institute, job title…" className="flex-1 text-sm bg-transparent focus:outline-none" />
        </div>
        <select data-testid="adm-jobapps-filter-institute" value={instituteFilter} onChange={(e) => { setInstituteFilter(e.target.value); setJobFilter(""); }} className="text-sm bg-white border border-[#e5e0d8] rounded-sm px-3 py-2 min-w-[220px]">
          <option value="">All Institutes ({items.length})</option>
          {institutes.map(([id, name]) => <option key={id} value={id}>{name} ({byInstitute(id)})</option>)}
        </select>
        <select data-testid="adm-jobapps-filter-job" value={jobFilter} onChange={(e) => setJobFilter(e.target.value)} className="text-sm bg-white border border-[#e5e0d8] rounded-sm px-3 py-2 min-w-[240px]" disabled={jobs.length === 0}>
          <option value="">All Jobs</option>
          {jobs.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b] py-6">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-sm border border-[#e5e0d8] p-14 text-center">
          <Inbox size={40} strokeWidth={1.2} className="mx-auto text-[#bcb6aa]" />
          <div className="mt-3 text-sm text-[#6b655b]">No applications match the current filters.</div>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f5f1e8] text-[10px] uppercase tracking-wider text-[#6b655b]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Candidate</th>
                <th className="text-left px-4 py-3 font-semibold">Applied For</th>
                <th className="text-left px-4 py-3 font-semibold">Institute</th>
                <th className="text-left px-4 py-3 font-semibold">Contact</th>
                <th className="text-left px-4 py-3 font-semibold">YOE</th>
                <th className="text-left px-4 py-3 font-semibold">Resume</th>
                <th className="text-left px-4 py-3 font-semibold">Received</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} data-testid={`adm-jobapp-row-${a.id}`} className="border-t border-[#e5e0d8] align-top">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#1c1a17]">{a.full_name}</div>
                    {a.current_profession && <div className="mt-1 text-[11px] text-[#6b655b] line-clamp-2 max-w-xs">{a.current_profession}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="inline-flex items-start gap-1.5 text-[#234333] font-medium">
                      <Briefcase size={12} className="mt-0.5 shrink-0" />
                      <span>{a.job_title || "General Application"}</span>
                    </div>
                    {a.job_status ? (
                      <div className="mt-1 inline-flex items-center text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-bold bg-[#f5f1e8] text-[#6b655b]">{a.job_status}</div>
                    ) : !a.job_id ? (
                      <div className="mt-1 inline-flex items-center text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-bold bg-amber-50 text-amber-800">Talent Pool</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <div className="inline-flex items-start gap-1.5 text-[#1c1a17]">
                      <Building2 size={12} className="mt-0.5 shrink-0 text-[#8b8578]" />
                      <span>{a.institute_name || "—"}</span>
                    </div>
                    {a.institute_slug && <div className="text-[10px] text-[#6b655b] mt-1">/{a.institute_slug}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <a href={`tel:${a.mobile}`} className="inline-flex items-center gap-1.5 text-[#234333] hover:text-[#d4a24c]"><Phone size={11} />{a.mobile}</a>
                    {a.email && <a href={`mailto:${a.email}`} className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-[#6b655b] hover:text-[#d4a24c] break-all"><Mail size={10} />{a.email}</a>}
                  </td>
                  <td className="px-4 py-3 font-semibold">{a.years_experience}</td>
                  <td className="px-4 py-3">
                    {a.resume_url ? (
                      <a href={a.resume_url} target="_blank" rel="noopener noreferrer" data-testid={`adm-jobapp-resume-${a.id}`} className="inline-flex items-center gap-1 text-[#234333] hover:text-[#d4a24c] text-[11px] font-semibold"><ExternalLink size={11} /> {a.resume_filename || "Resume.pdf"}</a>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-[#6b655b] whitespace-nowrap">
                    <div className="inline-flex items-center gap-1"><Calendar size={11} />{fmt(a.submitted_at)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  const cls = {
    navy: "bg-[#234333]/10 text-[#234333] border-[#234333]/30",
    blue: "bg-blue-50 text-blue-800 border-blue-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  }[accent] || "bg-white text-[#1c1a17] border-[#e5e0d8]";
  return (
    <div data-testid={testid} className={`border rounded-sm px-4 py-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="mt-1 text-2xl font-serif">{value}</div>
    </div>
  );
}
